import { RobotApi } from 'api/RobotApi'
import clsx from 'clsx'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import { ReactComponent as DropdownIcon } from 'images/icons/arrow_select.svg'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import store from 'store/store'
import { ROBOT_MODES } from 'utils/constants'

import {
  NTRIP_MOUNTPOINT_AUTO,
  NtripFormValues,
  ntripSaveBody,
  validateNtripForm,
  waitForNtripApplyConfirmation,
} from './ntripApplyConfirmation'

import style from './Service.module.css'

const EMPTY_FORM: NtripFormValues = {
  caster: '',
  port: '2101',
  user: '',
  password: '',
  mountpoint: NTRIP_MOUNTPOINT_AUTO,
  passwordSet: false,
}

type NtripSettingsDialogProps = {
  isOpen: boolean
  robotId?: number
  onClose: () => void
}

const NtripSettingsDialog = ({ isOpen, robotId, onClose }: NtripSettingsDialogProps) => {
  const { t } = useTranslation()
  const [form, setForm] = useState<NtripFormValues>(EMPTY_FORM)
  const [mountpoints, setMountpoints] = useState<string[]>([NTRIP_MOUNTPOINT_AUTO])
  const [mountpointMenuOpen, setMountpointMenuOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef(false)
  const mountpointWrapRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isOpen || robotId == null) {
      return
    }
    abortRef.current = false
    setError(null)
    setForm(EMPTY_FORM)
    setMountpointMenuOpen(false)
    setLoading(true)
    let cancelled = false
    RobotApi.getNtripSettings(robotId)
      .then(saved => {
        if (cancelled) {
          return
        }
        setForm({
          caster: saved.caster || '',
          port: saved.port != null ? String(saved.port) : '2101',
          user: saved.user || '',
          password: '',
          mountpoint: saved.mountpoint || NTRIP_MOUNTPOINT_AUTO,
          passwordSet: Boolean(saved.password_set),
        })
      })
      .catch(() => {
        if (!cancelled) {
          setError(t('robot.ntrip_load_failed'))
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, robotId, t])

  useEffect(() => {
    if (!isOpen || robotId == null) {
      return
    }
    const caster = form.caster.trim()
    if (!caster || /\s/.test(caster)) {
      setMountpoints([NTRIP_MOUNTPOINT_AUTO])
      return
    }
    let cancelled = false
    const timer = window.setTimeout(() => {
      RobotApi.getNtripMountpoints(robotId, caster)
        .then(names => {
          if (!cancelled) {
            setMountpoints(names.length ? names : [NTRIP_MOUNTPOINT_AUTO])
          }
        })
        .catch(() => {
          if (!cancelled) {
            setMountpoints([NTRIP_MOUNTPOINT_AUTO])
          }
        })
    }, 300)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [form.caster, isOpen, robotId])

  useEffect(() => {
    if (!mountpointMenuOpen) {
      return
    }
    const onPointerDown = (event: MouseEvent) => {
      if (!mountpointWrapRef.current?.contains(event.target as Node)) {
        setMountpointMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [mountpointMenuOpen])

  const mountpointOptions = useMemo(() => {
    const names = [...mountpoints]
    const current = form.mountpoint.trim()
    if (current && !names.includes(current)) {
      names.unshift(current)
    }
    if (!names.includes(NTRIP_MOUNTPOINT_AUTO)) {
      names.unshift(NTRIP_MOUNTPOINT_AUTO)
    }
    return names
  }, [form.mountpoint, mountpoints])

  const close = () => {
    abortRef.current = true
    setError(null)
    setMountpointMenuOpen(false)
    onClose()
  }

  const onApply = async () => {
    if (robotId == null) {
      return
    }
    const validation = validateNtripForm(form)
    if (validation) {
      setError(validation)
      return
    }
    setError(null)
    setMountpointMenuOpen(false)
    const expected = ntripSaveBody(form)
    try {
      await RobotApi.saveNtripSettings(robotId, expected)
      await RobotApi.switchMode(robotId, ROBOT_MODES.MODE_SWITCHING_NTRIP)
    } catch (err) {
      if (!abortRef.current) {
        setError(err instanceof Error ? err.message : t('robot.ntrip_request_failed'))
      }
      return
    }
    const outcome = await waitForNtripApplyConfirmation(
      expected,
      () => {
        const state = store.getState().robot
        return {
          telemetry: state.telemetry?.ntrip,
          feedback: state.robotInfo?.activeModeMetadata,
          pending: state.robotInfo?.modeSwitchPending,
        }
      },
      { isAborted: () => abortRef.current }
    )
    if (outcome === 'confirmed') {
      setError(null)
      onClose()
      return
    }
    if (outcome === 'timeout' && !abortRef.current) {
      setError(t('robot.ntrip_no_confirmation'))
    }
  }

  const field = (key: keyof NtripFormValues, value: string) => {
    setForm(current => ({ ...current, [key]: value }))
  }

  return (
    <ConfirmDialog
      title={t('robot.ntrip_dialog_title')}
      isOpen={isOpen}
      onClose={close}
      onConfirmClick={onApply}
      confirmText={t('robot.ntrip_apply')}
      confirmButtonDisabled={loading || Boolean(validateNtripForm(form))}
      danger={false}
      error={error}
    >
      <div className={style.ntripForm}>
        <div className={style.confirmField}>
          <label className={style.confirmLabel} htmlFor="ntrip-caster">
            {t('robot.ntrip_caster')}
          </label>
          <input
            id="ntrip-caster"
            className={style.confirmInput}
            value={form.caster}
            autoComplete="off"
            disabled={loading}
            onChange={event => field('caster', event.target.value)}
          />
        </div>
        <div className={style.confirmField}>
          <label className={style.confirmLabel} htmlFor="ntrip-port">
            {t('robot.ntrip_port')}
          </label>
          <input
            id="ntrip-port"
            className={style.confirmInput}
            value={form.port}
            inputMode="numeric"
            autoComplete="off"
            disabled={loading}
            onChange={event => field('port', event.target.value)}
          />
        </div>
        <div className={style.confirmField}>
          <label className={style.confirmLabel} htmlFor="ntrip-user">
            {t('robot.ntrip_user')}
          </label>
          <input
            id="ntrip-user"
            className={style.confirmInput}
            value={form.user}
            autoComplete="off"
            disabled={loading}
            onChange={event => field('user', event.target.value)}
          />
        </div>
        <div className={style.confirmField}>
          <label className={style.confirmLabel} htmlFor="ntrip-password">
            {t('robot.ntrip_password')}
          </label>
          <input
            id="ntrip-password"
            className={style.confirmInput}
            type="password"
            value={form.password}
            placeholder={form.passwordSet ? '••••••••' : ''}
            autoComplete="new-password"
            disabled={loading}
            onChange={event => field('password', event.target.value)}
          />
        </div>
        <div className={style.confirmField}>
          <label className={style.confirmLabel} htmlFor="ntrip-mountpoint">
            {t('robot.ntrip_mountpoint')}
          </label>
          <div className={style.ntripMountpointWrap} ref={mountpointWrapRef}>
            <div className={style.ntripMountpointInputRow}>
              <input
                id="ntrip-mountpoint"
                className={style.confirmInput}
                value={form.mountpoint}
                autoComplete="off"
                disabled={loading}
                onChange={event => field('mountpoint', event.target.value)}
                onFocus={() => setMountpointMenuOpen(true)}
              />
              <button
                type="button"
                className={style.ntripMountpointToggle}
                disabled={loading}
                aria-label={t('robot.ntrip_mountpoint')}
                aria-expanded={mountpointMenuOpen}
                onClick={() => setMountpointMenuOpen(open => !open)}
              >
                <DropdownIcon
                  className={clsx(
                    style.ntripMountpointToggleIcon,
                    mountpointMenuOpen && style.ntripMountpointToggleIconOpen
                  )}
                />
              </button>
            </div>
            {mountpointMenuOpen && (
              <div className={style.ntripMountpointMenu} role="listbox">
                {mountpointOptions.map(name => (
                  <button
                    key={name}
                    type="button"
                    role="option"
                    aria-selected={form.mountpoint === name}
                    className={clsx(
                      style.ntripMountpointOption,
                      form.mountpoint === name && style.ntripMountpointOptionActive
                    )}
                    onClick={() => {
                      field('mountpoint', name)
                      setMountpointMenuOpen(false)
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </ConfirmDialog>
  )
}

export default NtripSettingsDialog
